if (!customElements.get('sticky-buy-bar')) {
  class StickyBuyBar extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onCartUpdate = this.handleCartUpdate.bind(this);
      this.onCartError = this.handleCartError.bind(this);
      this.onSwatchClick = this.handleSwatchClick.bind(this);
      this.observer = null;
      this.unsubscribers = [];
      this.productData = null;
      this.productDataPromise = null;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productHandle = this.dataset.productHandle;
      this.sellingPlanId = this.dataset.sellingPlanId || '';
      this.hasPlan = this.dataset.hasPlan === 'true';
      this.fallbackOneTime = this.dataset.fallbackOneTime === 'true';

      this.variantInput = this.querySelector('[data-sticky-variant-input]');
      this.planInput = this.querySelector('[data-sticky-plan-input]');
      this.submitBtn = this.querySelector('.sticky-buy-bar__submit');
      this.submitBtnText = this.submitBtn ? this.submitBtn.querySelector('span') : null;
      this.priceCompare = this.querySelector('[data-sticky-compare]');
      this.priceCurrent = this.querySelector('[data-sticky-current]');
      this.priceSuffix = this.querySelector('[data-sticky-suffix]');
      this.priceSuffixText = this.priceSuffix ? this.priceSuffix.textContent.trim() : '';
      this.stickyImage = this.querySelector('[data-sticky-image]');
      this.swatches = Array.from(this.querySelectorAll('[data-sticky-swatches] .sticky-buy-bar__swatch'));
      this.selectedLabel = this.querySelector('[data-sticky-selected-label]');

      this.resolveMainForm();
      if (!this.mainSubmitButton) {
        return;
      }

      this.observeSubmitButton();
      this.swatches.forEach((el) => el.addEventListener('click', this.onSwatchClick));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, this.onCartError));
      }

      this.menuDetails = document.getElementById('Details-menu-drawer-container');
      if (this.menuDetails) {
        this.onMenuToggle = () => {
          this.classList.toggle('sticky-buy-bar--menu-open', this.menuDetails.open);
        };
        this.menuDetails.addEventListener('toggle', this.onMenuToggle);
      }

      this.ensureProductData();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.unsubscribers.forEach((fn) => typeof fn === 'function' && fn());
      this.unsubscribers = [];
      this.swatches.forEach((el) => el.removeEventListener('click', this.onSwatchClick));
      if (this.menuDetails && this.onMenuToggle) {
        this.menuDetails.removeEventListener('toggle', this.onMenuToggle);
      }
    }

    resolveMainForm() {
      const productInfo = document.querySelector('product-info');
      if (!productInfo) {
        this.mainSubmitButton = null;
        return;
      }
      this.productInfoEl = productInfo;
      this.mainSubmitButton = productInfo.querySelector('[id^="ProductSubmitButton-"]');
    }

    observeSubmitButton() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (!this.mainSubmitButton || !this.mainSubmitButton.isConnected) {
        this.resolveMainForm();
      }
      if (!this.mainSubmitButton) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.setVisible(!entry.isIntersecting);
        },
        { root: null, threshold: 0, rootMargin: '0px 0px -40px 0px' }
      );
      this.observer.observe(this.mainSubmitButton);
    }

    setVisible(visible) {
      this.classList.toggle('sticky-buy-bar--visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    handleSwatchClick(event) {
      const btn = event.currentTarget;
      const value = btn.dataset.optionValue;
      const variantId = btn.dataset.variantId;
      const available = btn.dataset.variantAvailable !== 'false';
      if (!value || !variantId) return;

      // Decoupled: update bar's own state only. Do NOT touch the main
      // variant-selects — doing so would trigger product-info's fetch +
      // focus-scroll-into-view, which jumps the page back up.
      if (this.variantInput) {
        this.variantInput.value = variantId;
      }
      this.markSwatchSelected(value);
      this.updateSubmitAvailability(available);
      this.updatePriceAndImageFromDataset(variantId);
    }

    markSwatchSelected(value) {
      this.swatches.forEach((el) => {
        const isMatch = el.dataset.optionValue === value;
        el.classList.toggle('is-selected', isMatch);
        el.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      });
      if (this.selectedLabel) {
        this.selectedLabel.textContent = value;
      }
    }

    updateSubmitAvailability(available) {
      if (!this.submitBtn) return;
      if (available) {
        this.submitBtn.removeAttribute('disabled');
      } else {
        this.submitBtn.setAttribute('disabled', 'disabled');
        if (this.submitBtnText && window.variantStrings && window.variantStrings.soldOut) {
          this.submitBtnText.textContent = window.variantStrings.soldOut;
        }
      }
    }

    updatePriceAndImageFromDataset(variantId) {
      this.ensureProductData().then(() => {
        if (!this.productData) return;
        const variant = this.productData.variants.find((v) => v.id === Number(variantId));
        if (!variant) return;

        if (this.stickyImage && variant.featured_image) {
          const src =
            typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
          if (src) this.stickyImage.src = src;
        }

        this.updatePriceForVariant(variantId);
      });
    }

    handleVariantChange(event) {
      // Dawn convention: payload arrives as { data: { variant, ... } }.
      const variant = event && event.data && event.data.variant;
      if (!variant) return;

      // Top-form changed → mirror into the bar (one-way sync).
      if (this.variantInput) this.variantInput.value = variant.id;

      if (this.stickyImage && variant.featured_image) {
        const src = variant.featured_image.src || variant.featured_image;
        if (src && typeof src === 'string') this.stickyImage.src = src;
      }

      if (variant.options && variant.options.length) {
        this.markSwatchSelected(variant.options[0]);
      }

      this.updateSubmitAvailability(!!variant.available);

      this.ensureProductData().then(() => this.updatePriceForVariant(variant.id));

      // Subtree may have been swapped — re-observe the submit button.
      this.resolveMainForm();
      this.observeSubmitButton();
    }

    ensureProductData() {
      if (this.productData) return Promise.resolve(this.productData);
      if (this.productDataPromise) return this.productDataPromise;
      if (!this.productHandle) return Promise.resolve(null);

      this.productDataPromise = fetch(`/products/${this.productHandle}.js`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          this.productData = data;
          return data;
        })
        .catch(() => null);
      return this.productDataPromise;
    }

    updatePriceForVariant(variantId) {
      if (!this.productData || !variantId) return;
      const variant = this.productData.variants.find((v) => v.id === Number(variantId));
      if (!variant) return;

      let currentPrice = variant.price;
      let comparePrice = variant.compare_at_price || variant.price;
      let showSuffix = false;
      let planActive = false;

      if (this.hasPlan && this.sellingPlanId) {
        const allocation = (variant.selling_plan_allocations || []).find(
          (a) => String(a.selling_plan_id) === String(this.sellingPlanId)
        );
        if (allocation) {
          currentPrice = allocation.price;
          comparePrice = allocation.compare_at_price || variant.price;
          showSuffix = true;
          planActive = true;
        }
      }

      if (this.planInput) {
        if (planActive) {
          this.planInput.disabled = false;
          this.planInput.value = this.sellingPlanId;
        } else if (this.fallbackOneTime) {
          this.planInput.disabled = true;
        }
      }

      if (this.priceCurrent) {
        this.priceCurrent.textContent = this.formatMoney(currentPrice);
      }
      if (this.priceCompare) {
        const shouldShow = comparePrice > currentPrice;
        if (shouldShow) {
          this.priceCompare.textContent = this.formatMoney(comparePrice);
          this.priceCompare.hidden = false;
        } else {
          this.priceCompare.hidden = true;
        }
      }
      if (this.priceSuffix) {
        if (showSuffix && this.priceSuffixText) {
          this.priceSuffix.textContent = this.priceSuffixText;
          this.priceSuffix.hidden = false;
        } else {
          this.priceSuffix.hidden = true;
        }
      }
    }

    formatMoney(cents) {
      if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      }
      const value = (cents / 100).toFixed(2).replace('.', ',');
      return `${value} kr.`;
    }

    handleCartUpdate() {
      if (!this.submitBtn) return;
      this.submitBtn.classList.remove('loading');
      this.submitBtn.removeAttribute('aria-disabled');
      const spinner = this.submitBtn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }

    handleCartError() {
      if (!this.submitBtn) return;
      this.submitBtn.classList.remove('loading');
      this.submitBtn.removeAttribute('aria-disabled');
      const spinner = this.submitBtn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
}
